import React, { useCallback, useEffect } from "react";
import "Common.css";
import "./PricingEngineDisplay.css";
import {
  c4A4A4A,
  cECECEC,
  c32BDC7,
  cWhite,
  cA4A4A4,
  cC9C9C9,
  c7C7C7C,
  UNDEF,
} from "Constants";
import {
  GeneratePricingResponse,
  Package,
  SelectedPricing,
  Surcharge,
} from "../../@types/PricingModels";
import { Div, Txt } from "Components/GenericView";
import { InputField } from "Components/InputField";
import { HelpRounded } from '@mui/icons-material';
import { Button, Checkbox, TextareaAutosize } from "@material-ui/core";
import { TextDropdown } from "Components/TextDropdown";
import {
  SURCHARGE_WAIVE_REASONS,
  SURCHARGE_DONOT_PAY_REASONS,
  OTHERS,
  isAU,
} from "DataConstants";
import { getCurrencySymbol, isBlank, isObjEmpty } from "Util/Utilities";

interface PricingEngineDisplayProps {
  pricing?: GeneratePricingResponse;
  paymentInstruction?: string;
  selectedPricing: SelectedPricing;
  visitCount?: number;
  urgentSurcharge?: Surcharge;
  transportSurcharge?: Surcharge;

  onPaymentInstChange: (field: any, instructions: string) => void;
  onPackageSelect: (pkg: Package) => void;
  onSurchargeUpdate: () => void;
  onApplyPricing: () => void;
}

export const PricingEngineDisplay: React.FC<PricingEngineDisplayProps> = (
  props
) => {
  const pricing = props.pricing;
  const sp = props.selectedPricing;
  const payg = pricing?.pay_as_you_go;
  const ownedPkgs = pricing?.owned_packages || [];
  const otherPkgs = pricing?.other_packages || [];
  const urgent = props.urgentSurcharge;
  const transport = props.transportSurcharge;
  const selUrgent = sp.urgent_surcharge || ({} as Surcharge);
  const selTransport = sp.transport_surcharge || ({} as Surcharge);

  const isApplyPricingDisabled = () => {
    if (
      !sp.package_id &&
      !sp.pay_as_you_go_package_id &&
      isBlank(sp.purchased_package_ids)
    ) {
      return true;
    }

    if (urgent && selUrgent.is_waived) {
      if (isBlank(selUrgent.waive_reason)) return true;
      if (
        selUrgent.waive_reason === OTHERS &&
        isBlank(selUrgent.waive_reason_details)
      )
        return true;

      if (selUrgent.do_not_pay_cp && isBlank(selUrgent.do_not_pay_cp_reason))
        return true;
      if (
        selUrgent.do_not_pay_cp_reason === OTHERS &&
        isBlank(selUrgent.do_not_pay_cp_reason_details)
      )
        return true;
    }

    if (transport && selTransport.is_waived) {
      if (isBlank(selTransport.waive_reason)) return true;
      if (
        selTransport.waive_reason === OTHERS &&
        isBlank(selTransport.waive_reason_details)
      )
        return true;
    }

    return false;
  };

  return (
    <Div v pt={50} ph={20} color={c4A4A4A} height="100%" pb={30} overflowY>
      <Div h vc>
        <Txt sb g vc fs={18}>
          How does the customer wants to pay?
        </Txt>

        <Txt l fs={14} textAlign="right">
          Total visits selected: {props.visitCount}
        </Txt>
      </Div>

      {!!pricing && <Div height={1} bg={cECECEC} mt={15} mb={20} w100 />}

      {!isObjEmpty(payg) && (
        <>
          <Txt sb fs={18} color={c32BDC7} mb={10}>
            A la carte
          </Txt>
          <PackagePill
            pkg={payg!}
            selected={!!sp.pay_as_you_go_package_id}
            onClick={props.onPackageSelect}
          />
        </>
      )}

      {ownedPkgs[0] && (
        <>
          <Txt h vc sb fs={18} color={c32BDC7} mb={10} mt={40}>
            Owned Packages
            <Txt className="ped-count">{ownedPkgs.length}</Txt>
          </Txt>

          <Div h pb={30} overflowX>
            {ownedPkgs.map((op) => (
              <PackagePill
                key={op.purchased_id}
                pkg={op}
                selected={sp.purchased_package_ids?.includes(
                  op.purchased_id ?? ""
                )}
                onClick={props.onPackageSelect}
              />
            ))}
          </Div>
        </>
      )}

      {otherPkgs[0] && (
        <>
          <Txt
            h
            vc
            sb
            fs={18}
            color={c32BDC7}
            mb={10}
            mt={ownedPkgs[0] ? 10 : 40}
          >
            Other Packages
            <Txt className="ped-count">{otherPkgs.length}</Txt>
          </Txt>

          <Div h pb={30} overflowX>
            {otherPkgs.map((op) => (
              <PackagePill
                key={op.package_id}
                pkg={op}
                selected={sp.package_id === op.package_id}
                onClick={props.onPackageSelect}
              />
            ))}
          </Div>
        </>
      )}

      {(urgent || transport) && (
        <Div w100 v pb={30} pt={otherPkgs[0] || ownedPkgs[0] ? 0 : 30}>
          <Div height={1} bg={cECECEC} mb={20} w100 />

          <Txt sb fs={18}>
            Surcharges
          </Txt>

          {urgent && (
            <SurchargeSection
              num={1}
              title="Urgent Surcharge"
              isUrgent
              surcharge={urgent}
              selected={selUrgent}
              onUpdate={props.onSurchargeUpdate}
            />
          )}

          {transport && (
            <SurchargeSection
              num={!urgent ? 1 : 2}
              title="Transport Surcharge"
              surcharge={transport}
              selected={selTransport}
              onUpdate={props.onSurchargeUpdate}
            />
          )}
        </Div>
      )}

      {!!pricing && (
        <>
          <Div
            height={1}
            bg={cECECEC}
            mb={20}
            w100
            mt={otherPkgs[0] || ownedPkgs[0] || urgent || transport ? 0 : 30}
          />
          <Div w100 v>
            <Txt m fs={15} mb={16}>
              Payment instruction
            </Txt>
            <TextareaAutosize
              className="ped-pi"
              placeholder="Are there any specific Payment Instructions?"
              onChange={(t) =>
                props.onPaymentInstChange(UNDEF, t.currentTarget.value)
              }
              minRows={3}
              value={props.paymentInstruction}
            />
          </Div>

          <Button
            className="ha-btn-green ped-apply-pricing"
            disabled={isApplyPricingDisabled()}
            onClick={props.onApplyPricing}
          >
            Apply Pricing
          </Button>
        </>
      )}
    </Div>
  );
};

interface PackagePillProps {
  pkg: Package;
  selected?: boolean;
  unit?: string;
  onClick: (pkg: Package) => void;
}
export const PackagePill: React.FC<PackagePillProps> = ({
  pkg,
  selected,
  onClick,
}) => {
  const unit = pkg.unit === "procedure" ? "/procedure" : "/h";
  const PKG = pkg.purchased_id
    ? PurchasedPill
    : pkg.save
      ? OtherPill
      : PAYGPill;
  const oc = useCallback(() => onClick(pkg), [onClick, pkg]);

  return <PKG pkg={pkg} selected={selected} unit={unit} onClick={oc} />;
};

// Pay as you go package
export const PAYGPill: React.FC<PackagePillProps> = ({
  pkg,
  selected,
  unit,
  onClick,
}) => {
  const bg = selected ? c32BDC7 : cWhite;
  const bc = selected ? cWhite : cECECEC;
  const tc = selected ? "#f3f3f3" : cA4A4A4;
  const pc = selected ? cWhite : c4A4A4A;
  const cn = "ped-pill transition";
  const lineBG = selected ? tc : cC9C9C9;

  return (
    <Div className={cn} bg={bg} bc={bc} onClick={onClick}>
      <Txt color={tc} m fs={9} pb={5} pr={3}>
        {pkg.currency}
      </Txt>
      <Txt color={pc} fs={18} sb>
        {pkg.price}
      </Txt>
      <Txt color={tc} fs={12} m ml={2} pt={1}>
        {unit}
      </Txt>

      <Div width={1} minH={22} height="100%" bg={lineBG} mh={8} />

      <Txt l color={tc} fs={14} lineHeight="19px">
        {pkg.name}
      </Txt>
    </Div>
  );
};

// Purchased package
export const PurchasedPill: React.FC<PackagePillProps> = ({
  pkg,
  selected,
  unit,
  onClick,
}) => {
  const bg = selected ? c32BDC7 : cWhite;
  const bc = selected ? cWhite : cECECEC;
  const tc = selected ? "#f3f3f3" : cA4A4A4;
  const nc = selected ? "#f3f3f3" : c7C7C7C;
  const pc = selected ? cWhite : c4A4A4A;
  const cn = "ped-pill ped-purchased-pill transition";
  const progress = (pkg.portion_used || 0) * 100 + "%";
  const fillBG = selected ? "#84D7DD" : "#f2f2f2";
  const progBG = selected ? cWhite : c32BDC7;

  return (
    <Div className={cn} bg={bg} bc={bc} ph={18} pv={13} onClick={onClick}>
      <Txt b color={nc} fs={11} lineHeight="15px">
        {pkg.name}
      </Txt>

      <Div h vc mt={2}>
        <Txt color={tc} m fs={9} pb={5} pr={3}>
          {pkg.currency}
        </Txt>
        <Txt color={pc} fs={18} sb>
          {pkg.price}
        </Txt>
        <Txt color={tc} fs={12} m ml={2} pt={1}>
          {unit}
        </Txt>
      </Div>

      <Txt color={tc} fs={12} m mt={4}>
        Expiry: {pkg.expiry}
      </Txt>

      <Div w100 bg={fillBG} height={5} br={2} mt={6}>
        <Div width={progress} bg={progBG} height={5} br={2} />
      </Div>
    </Div>
  );
};

// Other package
export const OtherPill: React.FC<PackagePillProps> = ({
  pkg,
  selected,
  unit,
  onClick,
}) => {
  const tc = selected ? "#f3f3f3" : cA4A4A4;
  const nc = selected ? "#f3f3f3" : c7C7C7C;
  const pc = selected ? cWhite : c4A4A4A;
  const lineBG = selected ? tc : cC9C9C9;
  const saveColor = selected ? "#ffe4b5" : "#FFBC49";

  return (
    <Div
      className="ped-pill transition"
      bg={selected ? c32BDC7 : cWhite}
      bc={selected ? cWhite : cECECEC}
      br={32}
      ph={20}
      pv={11}
      onClick={onClick}
    >
      <Txt color={tc} m fs={9} pb={5} pr={3}>
        {pkg.currency}
      </Txt>
      <Txt color={pc} fs={24} sb>
        {pkg.total_cost}
      </Txt>

      <Div width={1} minH={38} height="100%" bg={lineBG} mh={8} />

      <Div v>
        <Txt b color={nc} fs={11} lineHeight="15px">
          {pkg.name}
        </Txt>

        <Div h vc mt={3}>
          <Txt color={pc} fs={16} sb>
            {pkg.price}
          </Txt>

          <Txt color={tc} fs={12} m ml={2} pt={1}>
            {unit}
          </Txt>

          <Txt color={saveColor} fs={11} m ml={6} mt={2}>
            {pkg.save}
          </Txt>
        </Div>
      </Div>
    </Div>
  );
};

interface SurchargeSectionProps {
  num: number;
  title: string;
  surcharge: Surcharge;
  selected: Surcharge;
  isUrgent?: boolean;
  onUpdate: () => void;
}

export const SurchargeSection: React.FC<SurchargeSectionProps> = ({
  num,
  title,
  surcharge,
  selected,
  isUrgent,
  onUpdate,
}) => {
  const symbol = getCurrencySymbol();
  const isNDISShortVisit = surcharge?.is_waived && surcharge?.waive_reason === "Short NDIS visit" && isAU;

  const setWaiveValueAndReasonIfNDIS = () => {
    if (isNDISShortVisit && selected.is_waived === false) {
      selected.is_waived = surcharge.is_waived;
      selected.waive_reason = surcharge.waive_reason;
      onUpdate();
    };
  };


  useEffect(() => {
    setWaiveValueAndReasonIfNDIS();
  }, [selected?.is_waived]);

  const renderNormalCheckbox = () => {

    /* waive surcharge checkbox */
    return <Div
      className="h ha-checkbox-cont"
      onClick={() => {
        selected.is_waived = !selected.is_waived;
        onUpdate();
      }}
      mt={8}
      vc
    >
      <Checkbox
        style={{ display: "block" }}
        checked={!!selected.is_waived}
        className="ha-checkbox ha-checkbox-big"
      />
      <Txt fs={14} ml={4} pt={2}>
        Waive the {surcharge.name}
      </Txt>
    </Div>
  };

  const renderCheckboxForDisplayOnly = () => {

    return <Div
      className="h ha-checkbox-cont"
      mt={8}
      vc
    >
      <Checkbox
        style={{ display: "block" }}
        checked={true}
        disabled={true}
        className="ha-checkbox-disabled ha-checkbox-big"
      />
      <Txt fs={14} ml={4} pt={2}>
        Waive the {surcharge.name}
      </Txt>
    </Div>
  };

  const renderCheckbox = () => {
    if (isNDISShortVisit) {
      return renderCheckboxForDisplayOnly();
    };

    return renderNormalCheckbox();
  };

  const renderNormalDropdown = () => {
    if (!selected.is_waived) {
      return null;
    };

    {/* Waive reason and details  */ }
    return <Div h w100>
      <TextDropdown
        showDropdownArrow
        className="ped-dropdown"
        inputClassName="ped-td-inp"
        popupMaxHeight="280px"
        width="100%"
        lineHeight={19}
        items={SURCHARGE_WAIVE_REASONS}
        placeholder={`Reason for ${surcharge.name.toLocaleLowerCase()} waive`}
        selectedItem={selected.waive_reason}
        onChange={(_: any, txt: any) => {
          selected.waive_reason = txt || "";
          onUpdate();
        }}
      />

      {selected.waive_reason === OTHERS && (
        <InputField
          type="text"
          className="ped-urgent-reason"
          noAutoSelect
          lineHeight={19}
          width="100%"
          blurOnTabKey
          placeholder="State reason"
          onTextChange={(_: any, t: string) => {
            selected.waive_reason_details = t;
            onUpdate();
          }}
          text={selected.waive_reason_details}
        />
      )}
    </Div>
  };

  const renderDropdownForDisplayOnly = () => {
    const waive_reasons = [
      surcharge.waive_reason
    ];

    return <Div h w100>
      <TextDropdown
        showDropdownArrow
        className="ped-dropdown"
        inputClassName="ped-td-inp"
        popupMaxHeight="280px"
        width="100%"
        lineHeight={19}
        items={waive_reasons}
        placeholder={`Reason for ${surcharge.name.toLocaleLowerCase()} waive`}
        disabled={true}
        selectedItem={surcharge.waive_reason}
        onChange={(_: any, txt: any) => { }}
      />
    </Div>
  };


  const renderDropdown = () => {
    if (isNDISShortVisit) {
      return renderDropdownForDisplayOnly();
    };

    return renderNormalDropdown();
  };

  return (
    <Div h mt={20}>
      <Txt fs={14} sb mr={16}>
        {num}.
      </Txt>
      <Div v w100 flexStart>
        <Div fs={14} sb h>
          {title}
          <Div className="ped-tip-cont" pr={30} ml={8} h vc relative>
            <HelpRounded className="ped-surcharge-icon" fontSize="inherit" />
            <div className="ped-tip">
              <b>CO Charge:</b> {symbol}
              {surcharge.amount}
              <br />
              <b>CP Payout:</b> {symbol}
              {surcharge.payout_amount}
            </div>
          </Div>
        </Div>

        <Txt fs={14} sb mt={14}>
          {surcharge.name} - {symbol}
          {surcharge.amount}
        </Txt>

        {renderCheckbox()}

        {renderDropdown()}

        {isUrgent && selected.is_waived && (
          <>
            {/* waive surcharge checkbox */}
            <Div
              className="h ha-checkbox-cont"
              onClick={() => {
                selected.do_not_pay_cp = !selected.do_not_pay_cp;
                onUpdate();
              }}
              mt={8}
              vc
            >
              <Checkbox
                style={{ display: "block" }}
                checked={!!selected.do_not_pay_cp}
                className="ha-checkbox ha-checkbox-big"
              />
              <Txt fs={14} ml={4} pt={2}>
                Do not pay CP -{" "}
                <b>
                  {symbol}
                  {surcharge.payout_amount}
                </b>
              </Txt>
            </Div>
            {/* end waive surcharge checkbox */}

            {/* Waive reason and details  */}
            {!!selected.do_not_pay_cp && (
              <Div h w100>
                <TextDropdown
                  showDropdownArrow
                  className="ped-dropdown"
                  inputClassName="ped-td-inp"
                  popupMaxHeight="280px"
                  width="100%"
                  lineHeight={19}
                  items={SURCHARGE_DONOT_PAY_REASONS}
                  placeholder={`Reason for urgent CP payout waive`}
                  selectedItem={selected.do_not_pay_cp_reason}
                  onChange={(_: any, txt: any) => {
                    selected.do_not_pay_cp_reason = txt || "";
                    onUpdate();
                  }}
                />

                {selected.do_not_pay_cp_reason === OTHERS && (
                  <InputField
                    type="text"
                    className="ped-urgent-reason"
                    noAutoSelect
                    lineHeight={19}
                    width="100%"
                    blurOnTabKey
                    placeholder="State reason"
                    onTextChange={(_: any, t: string) => {
                      selected.do_not_pay_cp_reason_details = t;
                      onUpdate();
                    }}
                    text={selected.do_not_pay_cp_reason_details}
                  />
                )}
              </Div>
            )}
            {/* End Waive reason and details  */}
          </>
        )}
      </Div>
    </Div>
  );
};
